import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ArrowRight,
  CheckCheck,
  Clock3,
  Plus,
  CircleCheck,
  RefreshCw,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useDashboardStats, useEvents } from "../lib/queries";
import ActivityChart from "../components/dashboard/ActivityChart";
import RecentEvents from "../components/dashboard/RecentEvents";
import TaskProgress from "../components/dashboard/TaskProgress";

function UpcomingEvents({ events }) {
  const upcoming = events
    .filter(
      (event) =>
        event.status !== "closed" &&
        new Date(event.event_date_start).getTime() >= Date.now(),
    )
    .sort((a, b) => new Date(a.event_date_start) - new Date(b.event_date_start))
    .slice(0, 3);
  return (
    <section
      className="dashboard-panel upcoming-panel"
      aria-labelledby="upcoming-title"
    >
      <div className="panel-heading">
        <div>
          <h2 id="upcoming-title">Coming up next</h2>
          <p>Your next scheduled events</p>
        </div>
        <CalendarDays size={20} aria-hidden="true" />
      </div>
      {upcoming.length ? (
        <ul className="upcoming-list">
          {upcoming.map((event) => (
            <li key={event.id}>
              <Link to={`/events/${event.id}`}>
                <span className="upcoming-date">
                  <strong>{new Date(event.event_date_start).getDate()}</strong>
                  {new Date(event.event_date_start).toLocaleDateString("en", {
                    month: "short",
                  })}
                </span>
                <span className="upcoming-info">
                  <strong>{event.title}</strong>
                  <small>{event.institution_name}</small>
                </span>
                <ChevronRight size={16} />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="upcoming-empty">
          <span>
            <CircleCheck size={24} />
          </span>
          <h3>Your calendar is clear</h3>
          <p>
            No upcoming open events. Create an event when you're ready to plan
            your next shoot.
          </p>
          <Link className="text-action" to="/events?create=new">
            <Plus size={16} />
            Create an event
          </Link>
        </div>
      )}
      <Link to="/calendar" className="panel-footer-link">
        Open calendar <ArrowRight size={16} />
      </Link>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [months, setMonths] = useState(12);
  // Both lists come from the shared cache: coming back to the dashboard shows
  // the last numbers instantly and refreshes them quietly in the background.
  const statsQuery = useDashboardStats();
  const eventsQuery = useEvents();
  const data =
    statsQuery.data && eventsQuery.data
      ? { stats: statsQuery.data, events: eventsQuery.data }
      : null;
  const loading = statsQuery.isFetching || eventsQuery.isFetching;
  const initialLoading = !data && (statsQuery.isPending || eventsQuery.isPending);
  const error = statsQuery.isError || eventsQuery.isError;
  const lastUpdate = Math.max(statsQuery.dataUpdatedAt, eventsQuery.dataUpdatedAt);
  const updatedAt = lastUpdate ? new Date(lastUpdate) : null;
  const refresh = () => {
    statsQuery.refetch();
    eventsQuery.refetch();
  };

  const firstName = user?.name?.trim().split(" ")[0] || "there";
  const stats = data?.stats;
  const metrics = [
    {
      name: "Total events",
      value: stats?.total_events,
      icon: CalendarDays,
      note: "Across all institutions",
      className: "metric-primary",
      id: "stat-total-events",
    },
    {
      name: "Pending deliveries",
      value: stats?.pending_deliveries,
      icon: Clock3,
      note: "Open events + unfinished tasks",
      className: stats?.pending_deliveries ? "metric-gold" : "",
      id: "stat-pending-deliveries",
    },
    {
      name: "Completed tasks",
      value: stats?.completed_tasks,
      icon: CheckCheck,
      note: `Of ${stats?.total_tasks ?? 0} total tasks`,
      className: "metric-teal",
      id: "stat-completed-tasks",
    },
    {
      name: "Overdue tasks",
      value: stats?.overdue_tasks,
      icon: AlertCircle,
      note: stats?.overdue_tasks ? "Past their due date" : "No overdue work",
      className: stats?.overdue_tasks ? "metric-rose" : "",
      id: "stat-overdue-tasks",
    },
  ];
  return (
    <Layout>
      <div className="dashboard-page">
        <div className="dashboard-heading">
          <div>
            <h1>Workspace overview</h1>
            <p>
              Welcome back, <span className="user-greeting">{firstName}</span>.
              Here's your studio at a glance.
            </p>
          </div>
          <div className="dashboard-heading-actions">
            <time
              className="dashboard-date"
              dateTime={new Date().toLocaleDateString("en-CA")}
            >
              <CalendarDays size={16} />
              {new Date().toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </time>
            <Button asChild>
              <Link to="/events?create=new">
                <Plus size={17} />
                Create event
              </Link>
            </Button>
          </div>
        </div>
        {error && (
          <div className="dashboard-error" role="alert">
            <AlertCircle size={20} />
            <div>
              <strong>
                {data
                  ? "Unable to refresh your overview"
                  : "Unable to load your overview"}
              </strong>
              <p>
                {data
                  ? "Showing the last loaded data. Try refreshing again."
                  : "Check your connection and try again."}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={refresh}
              disabled={loading}
            >
              Try again
            </Button>
          </div>
        )}
        {initialLoading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading your workspace</h2>
            <p>Gathering your latest events and tasks.</p>
          </div>
        ) : (
          data && (
            <>
              <section
                className="dashboard-metrics"
                aria-label="Workspace statistics"
              >
                {metrics.map(
                  ({ name, value, icon: Icon, note, className, id }) => (
                    <article
                      className={`metric-card ${className}`}
                      key={id}
                      data-testid={id}
                    >
                      <div className="metric-top">
                        <h2>{name}</h2>
                        <span className="metric-icon">
                          <Icon size={19} strokeWidth={1.7} />
                        </span>
                      </div>
                      <strong>{(value ?? 0).toLocaleString()}</strong>
                      <p>{note}</p>
                    </article>
                  ),
                )}
              </section>
              <div className="dashboard-grid">
                <ActivityChart
                  events={data.events}
                  months={months}
                  onMonthsChange={setMonths}
                />
                <TaskProgress stats={data.stats} />
                <RecentEvents events={data.events} />
                <UpcomingEvents events={data.events} />
              </div>
              <footer className="dashboard-footer">
                <span>
                  {updatedAt
                    ? `Last updated at ${updatedAt.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}`
                    : ""}
                  {error ? " · Refresh needed" : ""}
                </span>
                <button
                  onClick={refresh}
                  disabled={loading}
                  aria-label="Refresh dashboard"
                >
                  <RefreshCw
                    size={14}
                    className={loading ? "animate-spin" : ""}
                  />
                  {loading ? "Refreshing…" : "Refresh data"}
                </button>
              </footer>
            </>
          )
        )}
      </div>
    </Layout>
  );
}
